import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

// Utils
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import cn from '../../../utils/cn';
import lockBodyScroll from '../../../utils/lockBodyScroll';

// Icons
import { ReactComponent as BurgerIcon } from '../../../assets/icons/burger.svg';

// Components
import Sidebar from '../Sidebar/Sidebar';
import TabsBar from '../TabsBar/TabsBar';
import BurgerMenu from '../../organisms/BurgerMenu/BurgerMenu';
import PageSection from '../PageSection/PageSection';

import styles from './LayoutWithSidebar.module.scss';

function LayoutWithSidebar({ Component, tabsBar, singleComponent }) {
  const { t } = useTranslation();
  const isSmallLaptop = useMediaQuery({ maxWidth: 1220 });

  const [burgerMenuIsOpened, setBurgerMenuIsOpened] = useState(false);

  useEffect(() => {
    lockBodyScroll(burgerMenuIsOpened);
  }, [burgerMenuIsOpened]);

  const getPageComponent = () => (
    <Component>
      {tabsBar ? (
        tabsBar.map((tab, index) => (
          <PageSection
            index={index}
            key={index}
            title={t(tab.label)}
            icon={tab.icon}
          >
            {tab.component}
          </PageSection>
        ))
      ) : singleComponent}
    </Component>
  );

  const desktopComponent = (
    <>
      <div className={styles.sidebar}>
        <Sidebar parentStyles={styles} />
      </div>

      <div className={cn([styles.content, 'd-flex a-stretch flex-auto'])}>
        {tabsBar ? (
          <TabsBar items={tabsBar} />
        ) : null}

        <div className={cn([styles.max_wrapper, 'p-40 flex-auto'])}>
          {getPageComponent()}
        </div>
      </div>
    </>
  );

  const smallLaptopComponent = (
    <div className="flex-auto">
      <div className={styles.sticky}>
        <div className="d-flex j-end p-10">
          <button
            type="button"
            onClick={() => setBurgerMenuIsOpened(!burgerMenuIsOpened)}
            className={cn([styles.burger_opener, 'd-flex a-center j-center'])}
          >
            <BurgerIcon />
          </button>
        </div>

        <BurgerMenu isOpened={burgerMenuIsOpened} onClose={() => setBurgerMenuIsOpened(false)}>
          <>
            {tabsBar ? (
              <TabsBar items={tabsBar} />
            ) : null}
            <Sidebar parentStyles={styles} />
          </>
        </BurgerMenu>
      </div>

      <div className="p-r-20 p-l-20 p-b-50">
        {getPageComponent()}
      </div>
    </div>
  );

  return (
    <section className="d-flex a-stretch">
      {isSmallLaptop ? smallLaptopComponent : desktopComponent}
    </section>
  );
}

LayoutWithSidebar.propTypes = {
  Component: PropTypes.func.isRequired,
  props: PropTypes.shape({}).isRequired,
  tabsBar: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})])),
  singleComponent: PropTypes.node,
};

LayoutWithSidebar.defaultProps = {
  tabsBar: null,
  singleComponent: null,
};

export default LayoutWithSidebar;
